import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import MemberSsoControl from './MemberSsoControl';
import { RefreshCw, Users, ShieldCheck, KeyRound, BarChart3 } from 'lucide-react';

/*
  C5 — per-member entity ACL + report-metric editor (owner/admin page).

  Backend contract:
    GET /api/workspace/members                      → member list
    GET /api/workspace/members/{id}/access          → { member_id, email, role,
        entity_access: { <key>: {mode, ids[]} }, allowed_report_metrics: [..],
        catalog: { entities, modes, default_mode, metrics } }
    PUT /api/workspace/members/{id}/access          → { entity_access?, allowed_report_metrics? }

  Enforcement (server-side): owners/admins are never restricted; a missing
  rule means "full"; an empty metric allow-list means "all metrics".
*/

// English labels for the backend catalog keys. These literals double as the
// i18n keys — they are passed through t() at render time.
const ENTITY_LABELS = {
  campaigns: 'Campaigns',
  offers: 'Offers',
  landing_pages: 'Landing pages',
  traffic_sources: 'Traffic sources',
  affiliate_networks: 'Affiliate networks',
};

const MODE_LABELS = {
  full: 'Full',
  read_only: 'Read-only',
  selected: 'To selected',
  created_selected: 'Created and selected',
};

const SCOPED_MODES = ['selected', 'created_selected'];

// Fallback for catalog keys this build does not know yet: "landing_pages"
// → "Landing pages". Known keys go through ENTITY_LABELS/MODE_LABELS above.
const humanize = (key) => {
  const s = String(key || '').split('_').join(' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// "id1, id2 id3;id4" → unique, order-preserving ["id1","id2","id3","id4"].
const parseIds = (text) => {
  const out = [];
  const seen = new Set();
  String(text || '')
    .split(/[\s,;]+/)
    .forEach((raw) => {
      const id = raw.trim();
      if (id && !seen.has(id)) {
        seen.add(id);
        out.push(id);
      }
    });
  return out;
};

export default function MemberAccess() {
  const { t } = useT();

  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState(false);

  const [selectedId, setSelectedId] = useState('');
  const [access, setAccess] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState(false);

  // Editor draft state, hydrated from the GET response.
  const [modes, setModes] = useState({});
  const [idsText, setIdsText] = useState({});
  const [metrics, setMetrics] = useState([]);
  const [saving, setSaving] = useState(false);

  // Guards against a slow response for a previously selected member
  // clobbering the editor after the user has already switched.
  const selectedRef = useRef('');

  const loadMembers = useCallback(async () => {
    try {
      setMembersLoading(true);
      setMembersError(false);
      const res = await axios.get(`${API}/workspace/members`);
      setMembers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error loading members:', err);
      setMembersError(true);
      toast.error(t('Failed to load members'));
    } finally {
      setMembersLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const loadAccess = useCallback(
    async (memberId) => {
      if (!memberId) return;
      try {
        setAccessLoading(true);
        setAccessError(false);
        const res = await axios.get(`${API}/workspace/members/${memberId}/access`);
        if (selectedRef.current !== memberId) return; // stale response
        const data = res.data || {};
        setAccess(data);
        const catalogEntities = data.catalog?.entities || [];
        const defaultMode = data.catalog?.default_mode || 'full';
        const nextModes = {};
        const nextIds = {};
        catalogEntities.forEach((key) => {
          const rule = data.entity_access?.[key];
          nextModes[key] = rule?.mode || defaultMode;
          nextIds[key] = Array.isArray(rule?.ids) ? rule.ids.join(', ') : '';
        });
        setModes(nextModes);
        setIdsText(nextIds);
        setMetrics(
          Array.isArray(data.allowed_report_metrics) ? data.allowed_report_metrics : []
        );
      } catch (err) {
        console.error('Error loading member access:', err);
        if (selectedRef.current === memberId) {
          setAccessError(true);
          toast.error(t('Failed to load member access'));
        }
      } finally {
        if (selectedRef.current === memberId) setAccessLoading(false);
      }
    },
    [t]
  );

  const pickMember = (id) => {
    setSelectedId(id);
    selectedRef.current = id;
    setAccess(null);
    setAccessError(false);
    loadAccess(id);
  };

  const refresh = () => {
    loadMembers();
    if (selectedId) loadAccess(selectedId);
  };

  const toggleMetric = (name) => {
    setMetrics((prev) =>
      prev.includes(name) ? prev.filter((m) => m !== name) : [...prev, name]
    );
  };

  const save = async () => {
    if (!selectedId || saving) return;
    const catalogEntities = access?.catalog?.entities || [];
    const defaultMode = access?.catalog?.default_mode || 'full';
    // Send the full map — the backend drops "full" entries as no-ops and
    // normalises modes/ids server-side.
    const entityAccess = {};
    catalogEntities.forEach((key) => {
      const mode = modes[key] || defaultMode;
      entityAccess[key] = {
        mode,
        ids: SCOPED_MODES.includes(mode) ? parseIds(idsText[key]) : [],
      };
    });
    setSaving(true);
    try {
      await axios.put(`${API}/workspace/members/${selectedId}/access`, {
        entity_access: entityAccess,
        allowed_report_metrics: metrics,
      });
      toast.success(t('Member access saved'));
      loadAccess(selectedId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('Failed to save member access'));
    } finally {
      setSaving(false);
    }
  };

  const selectedMember = members.find((m) => m.id === selectedId);
  const catalogEntities = access?.catalog?.entities || [];
  const catalogModes = access?.catalog?.modes || [];
  const catalogMetrics = access?.catalog?.metrics || [];
  const defaultMode = access?.catalog?.default_mode || 'full';
  const isUnrestrictedRole = ['owner', 'admin'].includes(
    String(access?.role || '').toLowerCase()
  );

  return (
    <div className="p-8 space-y-8 max-w-5xl">
      <XpnsrPageHeader
        kicker={t('Team')}
        title={t('Member access')}
        subtitle={t('Scope what each team member can see and edit — per entity, plus which report metrics they may read. Owners and admins are never restricted.')}
        actions={
          <Button variant="outline" onClick={refresh} disabled={membersLoading || accessLoading}>
            <RefreshCw className={'w-4 h-4 mr-2 ' + (membersLoading || accessLoading ? 'animate-spin' : '')} />
            {t('Refresh')}
          </Button>
        }
      />

      {membersLoading && members.length === 0 ? (
        <div className="skeleton h-40 w-full"></div>
      ) : membersError && members.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center space-y-3">
            <p className="text-sm text-muted-foreground">{t('Failed to load members')}</p>
            <Button variant="outline" size="sm" onClick={loadMembers}>{t('Retry')}</Button>
          </CardContent>
        </Card>
      ) : members.length === 0 ? (
        <Card>
          <CardContent className="py-10">
            <XpnsrEmpty
              icon={Users}
              title={t('No team members yet')}
              subtitle={t('Invite teammates from the Workspace page, then configure their access here.')}
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-full max-w-md">
                  <Label>{t('Member')}</Label>
                  <Select value={selectedId} onValueChange={pickMember}>
                    <SelectTrigger data-testid="member-access-picker">
                      <SelectValue placeholder={t('Choose a member…')}>
                        {selectedMember ? selectedMember.email : undefined}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {members.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          <span className="flex items-center gap-2">
                            <span className="truncate">{m.email}</span>
                            <Badge variant="outline" className="text-xs">{m.role}</Badge>
                            {m.status !== 'active' && (
                              <Badge variant="secondary" className="text-xs">{t('Invited')}</Badge>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {selectedMember && (
                  <div className="flex items-center gap-2 pb-2">
                    <Badge variant="outline">{selectedMember.role}</Badge>
                    {selectedMember.status === 'active' ? (
                      <Badge className="bg-primary/15 text-primary border border-primary/30">{t('Active')}</Badge>
                    ) : (
                      <Badge variant="outline">{t('Invited')}</Badge>
                    )}
                  </div>
                )}
                {selectedMember ? <MemberSsoControl member={selectedMember} /> : null}
              </div>
            </CardContent>
          </Card>

          {!selectedId ? (
            <Card>
              <CardContent className="py-10">
                <XpnsrEmpty
                  icon={KeyRound}
                  title={t('Pick a member to configure')}
                  subtitle={t('Their entity access and allowed report metrics will show up here.')}
                />
              </CardContent>
            </Card>
          ) : accessLoading && !access ? (
            <div className="skeleton h-96 w-full"></div>
          ) : accessError && !access ? (
            <Card>
              <CardContent className="py-8 text-center space-y-3">
                <p className="text-sm text-muted-foreground">{t('Failed to load member access')}</p>
                <Button variant="outline" size="sm" onClick={() => loadAccess(selectedId)}>{t('Retry')}</Button>
              </CardContent>
            </Card>
          ) : access ? (
            <>
              {isUnrestrictedRole && (
                <div
                  className="flex items-start gap-3 p-4 rounded-md border border-amber-500/30 bg-amber-500/10"
                  role="note"
                  data-testid="unrestricted-role-banner"
                >
                  <ShieldCheck className="w-5 h-5 shrink-0 text-amber-500" />
                  <div className="text-sm">
                    <p className="font-medium">{t('Owners and admins have full, unrestricted access')}</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      {t('You can still save these rules, but they stay inert unless the role changes to manager or viewer.')}
                    </p>
                  </div>
                </div>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <KeyRound className="w-4 h-4" /> {t('Entity access')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <p className="text-xs text-muted-foreground px-6 pb-4">
                    {t('Full is the default: no restriction. Read-only blocks edits. To selected limits the member to the listed IDs. Created and selected adds anything the member created themselves.')}
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('Entity')}</TableHead>
                        <TableHead>{t('Access mode')}</TableHead>
                        <TableHead>{t('Allowed IDs')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {catalogEntities.map((key) => {
                        const entityLabel = t(ENTITY_LABELS[key] || humanize(key));
                        const mode = modes[key] || defaultMode;
                        const scoped = SCOPED_MODES.includes(mode);
                        const idCount = parseIds(idsText[key]).length;
                        return (
                          <TableRow key={key} data-testid={`entity-row-${key}`}>
                            <TableCell className="font-medium whitespace-nowrap">{entityLabel}</TableCell>
                            <TableCell className="w-56 align-top">
                              <Select
                                value={mode}
                                onValueChange={(v) => setModes((prev) => ({ ...prev, [key]: v }))}
                              >
                                <SelectTrigger
                                  aria-label={`${t('Access mode')} — ${entityLabel}`}
                                  data-testid={`access-mode-${key}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {catalogModes.map((m) => (
                                    <SelectItem key={m} value={m}>
                                      {t(MODE_LABELS[m] || humanize(m))}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="align-top">
                              {scoped ? (
                                <div className="space-y-1">
                                  <Input
                                    value={idsText[key] || ''}
                                    onChange={(e) =>
                                      setIdsText((prev) => ({ ...prev, [key]: e.target.value }))
                                    }
                                    placeholder={t('IDs, comma or space separated')}
                                    aria-label={`${t('Allowed IDs')} — ${entityLabel}`}
                                    data-testid={`entity-ids-${key}`}
                                  />
                                  <p className="text-xs text-muted-foreground">
                                    {t('IDs of the specific items this member may access')}
                                  </p>
                                  {idCount === 0 && (
                                    <p className="text-xs text-amber-500">
                                      {mode === 'created_selected'
                                        ? t('No IDs yet — the member will only see items they created themselves.')
                                        : t('No IDs yet — the member will not see any items of this type.')}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-muted-foreground">{t('All items')}</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" /> {t('Allowed report metrics')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {t('Toggle the metrics this member may see in reports and dashboards. Leaving every metric off means no restriction.')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {catalogMetrics.map((m) => {
                      const on = metrics.includes(m);
                      return (
                        <Button
                          key={m}
                          type="button"
                          size="sm"
                          variant={on ? 'default' : 'outline'}
                          aria-pressed={on}
                          onClick={() => toggleMetric(m)}
                          className="h-7 px-2.5 font-mono text-xs"
                          data-testid={`metric-chip-${m}`}
                        >
                          {m}
                        </Button>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    {metrics.length === 0 ? (
                      <Badge variant="outline">{t('All metrics (no restriction)')}</Badge>
                    ) : (
                      <Badge variant="outline">
                        {metrics.length} / {catalogMetrics.length} {t('metrics allowed')}
                      </Badge>
                    )}
                    <Button type="button" variant="ghost" size="sm" onClick={() => setMetrics([...catalogMetrics])}>
                      {t('Select all')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setMetrics([])}
                      disabled={metrics.length === 0}
                    >
                      {t('Clear (allow all)')}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <div className="flex items-center justify-end">
                <Button onClick={save} disabled={saving || accessLoading} data-testid="save-member-access">
                  {saving ? t('Saving…') : t('Save access')}
                </Button>
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
